import { PortfolioAttachment } from './../../shared/models/portfolio-attachment';
import { Certificate } from './../../shared/models/certificate';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Profile } from '../../shared/models/profile';
import { Education } from '../../shared/models/education';
import { Experience } from '../../shared/models/experience';
import { ProfileSkill } from '../../shared/models/profile-skill';
import { Resume } from '../../shared/models/resume';
import { ExperienceAttachment } from '../../shared/models/experience-attachment';

@Injectable({
  providedIn: 'root',
})
export class CandidateStore {

  _profile: BehaviorSubject<Profile> = new BehaviorSubject(null);
  public readonly userProfile: Observable<Profile> =
    this._profile.asObservable();

  _profilePhoto: BehaviorSubject<string> = new BehaviorSubject(null);
  public readonly profilePhoto: Observable<string> =
    this._profilePhoto.asObservable();

  _educations: BehaviorSubject<Education[]> = new BehaviorSubject([]);
  public readonly educations: Observable<Education[]> =
    this._educations.asObservable();

  _experiences: BehaviorSubject<Experience[]> = new BehaviorSubject([]);
  public readonly experiences: Observable<Experience[]> =
    this._experiences.asObservable();

  _skills: BehaviorSubject<ProfileSkill[]> = new BehaviorSubject([]);
  public readonly skills: Observable<ProfileSkill[]> =
    this._skills.asObservable();

  _certifications: BehaviorSubject<Certificate[]> = new BehaviorSubject([]);
  public readonly certifications$: Observable<Certificate[]> =
    this._certifications.asObservable();

  _portfolios: BehaviorSubject<PortfolioAttachment[]> = new BehaviorSubject([]);
  public readonly portoflios$: Observable<PortfolioAttachment[]> =
    this._portfolios.asObservable();

  _openPortfolioDialog: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public readonly openPortfolioDialog$: Observable<boolean> =
    this._openPortfolioDialog.asObservable();

  _resume: BehaviorSubject<Resume> = new BehaviorSubject(null);
  public readonly resume: Observable<Resume> = this._resume.asObservable();

  private resumeImage = new Subject();
  public resumeImage$ = this.resumeImage.asObservable();

  changeImage(obj) {
    this.resumeImage.next(obj);
  }
  constructor() {
  }

  public setProfile(profile: Profile): void {
    this._profile.next(profile);
    if (profile.photoPath) {
      this.setProfilePath(profile.photoPath);
    }
  }

  public getEducations(): Education[] {
    return this._educations.getValue();
  }

  public addEducation(education: Education): void {
    this._educations.getValue().push(education);
  }

  public updateEducation(education: Education, index: number): void {
    this._educations.getValue()[index] = education;
  }

  public removeEducation(index: number): void {
    this._educations.getValue().splice(index, 1);
    let newArray = this._educations.getValue();
    this._educations.next(newArray);
  }

  public getExperiences(): Experience[] {
    return this._experiences.getValue();
  }

  public addExperience(experience: Experience): void {
    this._experiences.getValue().push(experience);
  }

  addExperienceAttachment = (experienceAttachment: ExperienceAttachment) => {
    const experiences = this._experiences.getValue();
    const index = experiences.findIndex(
      (x) => x.id == experienceAttachment.experienceId
    );
    let experience = new Experience();
    experience.hidden = true;
    if (index < 0) {
      experience.id = experienceAttachment.experienceId;
    } else {
      experience = experiences[index];
    }
    var attachments = experience.attachments;
    if (!attachments || attachments.length == 0) {
      attachments = [];
      attachments.push(experienceAttachment);
    } else {
      const existingIndex = attachments.findIndex(
        (x) => x.attachmentId == experienceAttachment.attachmentId
      );
      if (existingIndex > 0) {
        attachments[existingIndex] = experienceAttachment;
      } else {
        attachments.push(experienceAttachment);
      }
    }
    experience.attachments = attachments;
    if (index < 0) {
      this._experiences.getValue().push(experience);
    } else {
      this._experiences.getValue()[index] = experience;
    }
  };

  public updateExperience(experience: Experience, index: number): void {
    this._experiences.getValue()[index] = experience;
  }

  public removeExperience(index: number): void {
    this._experiences.getValue().splice(index, 1);
    let newArray = this._experiences.getValue();
    this._experiences.next(newArray);
  }

  public getCertifications(): Certificate[] {
    return this._certifications.getValue();
  }

  public addCertificate(certificate: Certificate): void {
    this._certifications.getValue().push(certificate);
  }

  public initialiseCertificates(): void {
    if (this._certifications.getValue().length > 0)
      return;
    this.addCertificate(new Certificate());
  }

  public updateListCertificate = (certificate: Certificate) => {
    const count = this._certifications.getValue().length;
    this._certifications.getValue()[count - 1] = certificate;
  };

  public updateCertificate = (index: number, certificate: Certificate) => {
    this._certifications.getValue()[index] = certificate;
  };

  public removeCertificate(index: number): void {
    this._certifications.getValue().splice(index, 1);
    const updatedList = this._certifications.getValue();
    this._certifications.next(updatedList);
  }

  public getPortfolios(): PortfolioAttachment[] {
    return this._portfolios.getValue();
  }

  public addPortfolios(portfolios: PortfolioAttachment[]): void {
    this._portfolios.next(portfolios);
  }

  public addPortfolio(portfolio: PortfolioAttachment): void {
    let portfolioAttachments = this._portfolios.getValue();
    portfolioAttachments.push(portfolio);

    this._portfolios.next(portfolioAttachments);
  }

  public deletePortfolioAttachment(attachmentId: string): void {
    const existingArray = this._portfolios.getValue();
    this._portfolios.getValue().splice(existingArray.findIndex(x => x.attachmentId === attachmentId), 1);
    const newArray = this._portfolios.getValue();
    this._portfolios.next(newArray);
  }

  public setPortfolioDialogState = (flag: boolean) => {
    this._openPortfolioDialog.next(flag);
  };

  public getSkills(): ProfileSkill[] {
    return this._skills.getValue();
  }

  public addSkill(skill: ProfileSkill): void {
    this._skills.getValue().push(skill);
  }

  public initialiseSkills(): void {
    if (this._skills.getValue().length > 0)
      return;
    this.addSkill(new ProfileSkill());
  }

  public updateListSkill(skill: ProfileSkill): void {
    let skillCount = this._skills.getValue().length;
    this._skills.getValue()[skillCount - 1] = skill;
  }

  public removeSkill(index: number): void {
    this._skills.getValue().splice(index, 1);
    let newArray = this._skills.getValue();
    this._skills.next(newArray);
  }

  public setResume(resume: Resume): void {
    this._resume.next(resume);

    if (resume.educations) {
      this._educations.next(resume.educations);
    }

    if (resume.experiences) {
      this._experiences.next(resume.experiences);
    }

    if (resume.skills) {
      this._skills.next(resume.skills);
    }

    if (resume.certificates) {
      this._certifications.next(resume.certificates);
    }

    if (resume.portfolioAttachments) {
      this._portfolios.next(resume.portfolioAttachments);
    }
  }

  public setProfilePath(path: string): void {
    if (this._profilePhoto.getValue() == path) {
      return;
    }

    const profile = this._profile.getValue();
    if (profile) {
      profile.photoPath = path;
    }

    this._profilePhoto.next(path);
  }

  public saveResume(): void {

  }

}
